import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import type { Department } from '../data/intelligenceTier';
import { renderEmphasis } from '../lib/text';

const toSlug = (name: string) => name.toLowerCase().replace(/\s+/g, '-');

export default function DepartmentCard({ dept }: { dept: Department }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const isNew = dept.badgeType === 'new';
  const isMerged = dept.badgeType === 'merged';
  const deptId = `dept-${toSlug(dept.name)}`;

  useEffect(() => {
    const slug = toSlug(dept.name);
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail.slug === slug) setIsExpanded(true);
    };
    window.addEventListener('expand-dept', handler);
    return () => window.removeEventListener('expand-dept', handler);
  }, [dept.name]);

  const railColor = isNew ? 'before:bg-sky-deep' : 'before:bg-green-700';

  const cardClasses = [
    'relative rounded-lg border bg-white cursor-pointer overflow-hidden transition-shadow duration-200',
    isExpanded
      ? `border-green-700 shadow-elev-2 before:content-[""] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] ${railColor} before:rounded-l-lg`
      : 'border-border hover:border-border-strong',
  ].join(' ');

  const badgeClasses = isNew
    ? 'bg-sky-tint text-sky-deep'
    : isMerged
    ? 'bg-paper-deep text-ink-mute'
    : 'bg-green-50 text-green-700';

  return (
    <article id={deptId} onClick={() => setIsExpanded((v) => !v)} className={cardClasses}>
      <div className="px-6 pt-5 pb-4">
        <div className="mb-3">
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.06em] px-2.5 py-[3px] rounded-full whitespace-nowrap ${badgeClasses}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {isNew ? 'New function' : isMerged ? 'Merged' : dept.badgeText.replace(/^Tier\s\d+\s·\s/, '')}
          </span>
        </div>
        <h3 className="font-display text-[22px] font-semibold leading-tight tracking-tight text-ink mb-1.5">
          {dept.name}
        </h3>
        <p className="text-[13px] text-ink-mute leading-[1.55]">
          {dept.tagline}
        </p>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
              opacity: { duration: 0.18, delay: 0.05 },
            }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-6 py-5 border-t border-divider bg-paper-light rounded-b-lg">
              {dept.absorbs && dept.absorbs.length > 0 ? (
                <div className="mb-5">
                  <SectionLabel>Absorbs</SectionLabel>
                  <div className="flex flex-wrap gap-1.5">
                    {dept.absorbs.map((tag) => (
                      <span
                        key={tag}
                        className="text-[12px] bg-white border border-border rounded px-2.5 py-[3px] text-stone-500 line-through decoration-stone-400 decoration-1"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mb-5">
                  <SectionLabel>Has no predecessor</SectionLabel>
                </div>
              )}

              <div className="mb-5">
                <SectionLabel>What it does</SectionLabel>
                <p className="text-[14px] leading-[1.7] text-ink-soft">
                  {renderEmphasis(dept.description)}
                </p>
              </div>

              <div>
                <SectionLabel>Why it matters</SectionLabel>
                <p className={`text-[13px] leading-[1.7] text-ink-soft italic px-5 py-4 rounded-md border-l-2 ${isNew ? 'bg-sky-tint border-sky-deep' : 'bg-green-50 border-green-700'}`}>
                  {dept.why}
                </p>
              </div>

              <div className="mt-5 pt-4 border-t border-divider">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const target = document.getElementById('email-gate')
                      ?? document.getElementById('diagnosis-section');
                    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    setTimeout(() => {
                      (document.getElementById('email-gate')?.querySelector('textarea') as HTMLTextAreaElement | null)?.focus();
                    }, 600);
                  }}
                  className={`inline-flex items-center gap-2 text-[13px] border rounded-md px-4 py-2.5 cursor-pointer transition-colors ${
                    isNew
                      ? 'bg-white border-border-strong text-ink-soft hover:bg-sky-tint hover:border-sky-deep hover:text-sky-deep'
                      : 'bg-white border-border-strong text-ink-soft hover:bg-green-50 hover:border-green-700 hover:text-green-700'
                  }`}
                >
                  <ArrowRight size={14} />
                  {dept.ctaText}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isExpanded && (
        <div className="flex items-center justify-end px-6 py-3 border-t border-divider">
          <ChevronDown size={14} className="text-stone-400" />
        </div>
      )}
    </article>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-semibold tracking-mega uppercase text-stone-500 mb-3">
      {children}
    </div>
  );
}
